<template>
  <div
    ref="searchSelect"
    class="base-search-select"
    @keydown.arrow-up.prevent="updateActiveIndex('prev')"
    @keydown.arrow-down.prevent="updateActiveIndex()"
    @keydown.esc="$emit('close')">
    <oie-base-input-v2
      :id="`${id}-input`"
      v-model="searchQuery"
      :aria-controls="`${id}-listbox`"
      :aria-expanded="isExpanded"
      :label="label || 'Search for options'"
      v-bind="$attrs">
      <template #iconPrepend>
        <oie-base-icon icon="search" size="s"></oie-base-icon>
      </template>
    </oie-base-input-v2>
    <ul
      v-show="isExpanded"
      :id="`${id}-listbox`"
      class="base-search-select__list"
      role="listbox">
      <li v-if="noOptionsAvailable" class="base-search-select__option">
        Ingen traff
      </li>
      <li
        v-for="(option, i) in options"
        :id="`${id}-option-${i}`"
        :key="`option-${i}`"
        class="base-search-select__option"
        role="option"
        tabindex="0"
        @click="$emit('select', option)"
        @keydown.enter="$emit('select', option)">
        {{ option.label }}
      </li>
    </ul>
  </div>
</template>

<script>
import { defineComponent, computed, ref, watch } from '@vue/composition-api'
import { onClickOutside } from '@vueuse/core'
import _ from 'lodash'

export default defineComponent({
  props: {
    label: {
      type: String,
      default: ''
    },
    options: {
      type: Array,
      default: null
    }
  },
  setup(props, { emit }) {
    const searchSelect = ref(null)
    onClickOutside(searchSelect, () => { emit('close') })
    const id = _.uniqueId('base-search-select-')
    const activeIndex = ref(-1)
    const searchQuery = ref('')

    const isExpanded = computed(() => props.options)
    const noOptionsAvailable = computed(() => props.options && !props.options.length)

    watch(searchQuery, () => { emit('search', searchQuery.value) })
    watch(isExpanded, (newVal) => {
      if(!newVal) searchQuery.value = ''
    })

    function updateActiveIndex(direction = 'next') {
      if (!isExpanded.value) return
      if (!props.options.length) return

      const newIndex = direction === 'next' ? activeIndex.value + 1 : activeIndex.value - 1
      const validIndex = props.options[newIndex]
      if (validIndex) {
        activeIndex.value = newIndex
      } else {
        activeIndex.value = (direction === 'next' ? 0 : props.options.length - 1)
      }
      const optionElement = document.getElementById(`${id}-option-${activeIndex.value}`)
      if (optionElement) optionElement.focus()
    }

    return {
      searchSelect,
      id,
      isExpanded,
      noOptionsAvailable,
      updateActiveIndex,
      searchQuery
    }
  }
})
</script>

<style lang="scss">
.base-search-select {
  position: relative;
}

.base-search-select__list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid color(border-gray);
  max-height: 20rem;
  z-index: 2;
  overflow-y: auto;
}

.base-search-select__option {
  padding: 8px;
  cursor: pointer;
}

.base-search-select__option:hover,
.base-search-select__option:focus {
  background-color: color(blue-4);
}
</style>
